import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useShallow } from 'zustand/shallow'

/**
 * This is where you will add you feature flags to be used within the application.
 * When naming feature flags, opt for naming the same as the corresponding epic.
 * i.e: For epic "Neat Feature V1" name the flag -> "neatFeatureV1"
 *
 * Note that when adding a flag, create a corresponding ticket for removing the flag
 * all together.
 */
export const FEATURE_FLAGS = [] as const

export const STORAGE_KEY = 'featureFlag-storage'

export type FeatureFlagName = (typeof FEATURE_FLAGS)[number]

type FeatureFlagStore = {
  flags: Record<FeatureFlagName, boolean>
  toggleFlag: (flagName: FeatureFlagName) => void
}

const initFlags = () => {
  const flags: Record<string, boolean> = {}
  FEATURE_FLAGS.forEach(featureFlagName => {
    flags[featureFlagName] = false
  })
  return flags
}
const useFeatureFlagStore = create<FeatureFlagStore>()(
  persist(
    set => ({
      flags: initFlags(),
      toggleFlag: (flagName: FeatureFlagName) => {
        set(state => ({
          flags: {
            ...state.flags,
            [flagName]: !state.flags[flagName],
          },
        }))
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export const useFeatureFlags = () => {
  const { flags, toggleFlag } = useFeatureFlagStore(
    useShallow(state => ({
      flags: state.flags,
      toggleFlag: state.toggleFlag,
    })),
  )

  return {
    flags,
    isFeatureEnabled: (flagName: FeatureFlagName) => flags[flagName] || false,
    toggleFlag,
  }
}

export default useFeatureFlags
